import { GameMode, BotProfile } from '../engine/types';
import { RoomSession } from '../firebase/session';
import { t } from '../i18n';

export interface LobbyCallbacks {
  onStartSolo: (mode: GameMode, botProfile: BotProfile) => void;
  onCreateRoom: (mode: GameMode, isPublic: boolean) => void;
  onJoinRoom: (roomCode: string) => void;
  onStartOnlineGame: () => void;
  onAddBotToRoom: (profile: BotProfile) => void;
  onToggleRoomPublic: () => void;
  onLeaveRoom: () => void;
  onRefreshPublicRooms: () => void;
}

export class Lobby {
  private container: HTMLElement;
  private callbacks: LobbyCallbacks;
  private currentTab: 'solo' | 'multiplayer' = 'solo';
  private selectedMode: GameMode = 'duel';
  private selectedBotProfile: BotProfile = 'minimax';
  private activeSession: RoomSession | null = null;
  private localPlayerId: string = '';
  private publicRooms: RoomSession[] = [];

  constructor(container: HTMLElement, callbacks: LobbyCallbacks) {
    this.container = container;
    this.callbacks = callbacks;
  }

  public setLocalPlayer(playerId: string): void {
    this.localPlayerId = playerId;
  }

  public setSession(session: RoomSession | null): void {
    this.activeSession = session;
    this.render();
  }

  public setPublicRooms(rooms: RoomSession[]): void {
    this.publicRooms = rooms;
    if (this.currentTab === 'multiplayer' && !this.activeSession) {
      this.render();
    }
  }

  public render(): void {
    this.container.innerHTML = '';

    const lobbyView = document.createElement('div');
    lobbyView.className = 'lobby-view';

    const lobbyBox = document.createElement('div');
    lobbyBox.className = 'lobby-box';

    // Hero Section
    const hero = document.createElement('div');
    hero.className = 'lobby-hero';
    hero.innerHTML = `
      <img src="./assets/Logo.png" alt="Slimelings Bit-It!" class="lobby-logo" />
      <p class="lobby-slogan">"${t('app_slogan')}"</p>
    `;
    lobbyBox.appendChild(hero);

    if (this.activeSession) {
      // In a waiting room
      this.renderWaitingRoom(lobbyBox, this.activeSession);
    } else {
      // Navigation Tabs
      const tabsNav = document.createElement('div');
      tabsNav.className = 'tabs-nav';

      const soloTab = document.createElement('button');
      soloTab.className = `tab-btn ${this.currentTab === 'solo' ? 'active' : ''}`;
      soloTab.textContent = t('tab_solo');
      soloTab.onclick = () => {
        this.currentTab = 'solo';
        this.render();
      };

      const multiTab = document.createElement('button');
      multiTab.className = `tab-btn ${this.currentTab === 'multiplayer' ? 'active' : ''}`;
      multiTab.textContent = t('tab_multiplayer');
      multiTab.onclick = () => {
        this.currentTab = 'multiplayer';
        this.callbacks.onRefreshPublicRooms();
        this.render();
      };

      tabsNav.appendChild(soloTab);
      tabsNav.appendChild(multiTab);
      lobbyBox.appendChild(tabsNav);

      if (this.currentTab === 'solo') {
        this.renderSoloConfig(lobbyBox);
      } else {
        this.renderMultiplayerConfig(lobbyBox);
      }
    }

    lobbyView.appendChild(lobbyBox);
    this.container.appendChild(lobbyView);
  }

  private renderSoloConfig(parent: HTMLElement): void {
    const modeSection = document.createElement('div');
    modeSection.className = 'mode-selector';

    const modes: Array<{ mode: GameMode; title: string; desc: string }> = [
      { mode: 'duel', title: t('mode_duel'), desc: t('mode_duel_desc') },
      { mode: 'teams', title: t('mode_teams'), desc: t('mode_teams_desc') },
      { mode: 'ffa', title: t('mode_ffa'), desc: t('mode_ffa_desc') },
    ];

    modes.forEach((m) => {
      const card = document.createElement('div');
      card.className = `mode-card ${this.selectedMode === m.mode ? 'selected' : ''}`;
      card.innerHTML = `
        <div>
          <div class="mode-name">${m.title}</div>
          <div class="mode-desc">${m.desc}</div>
        </div>
        <div style="font-size: 18px;">${this.selectedMode === m.mode ? '●' : '○'}</div>
      `;
      card.onclick = () => {
        this.selectedMode = m.mode;
        this.render();
      };
      modeSection.appendChild(card);
    });

    // Bot Personality Picker
    const botLabel = document.createElement('label');
    botLabel.style.fontSize = '13px';
    botLabel.style.fontWeight = '700';
    botLabel.textContent = `${t('player_bot')}:`;

    const botSelect = document.createElement('select');
    botSelect.className = 'lang-select';
    botSelect.style.width = '100%';

    const profiles: Array<{ id: BotProfile; label: string }> = [
      { id: 'cautious', label: t('bot_level_1') },
      { id: 'minimax', label: t('bot_level_2') },
      { id: 'aggressive', label: t('bot_level_3') },
    ];

    profiles.forEach((p) => {
      const opt = document.createElement('option');
      opt.value = p.id;
      opt.textContent = p.label;
      if (p.id === this.selectedBotProfile) opt.selected = true;
      botSelect.appendChild(opt);
    });

    botSelect.onchange = () => {
      this.selectedBotProfile = botSelect.value as BotProfile;
    };

    const startBtn = document.createElement('button');
    startBtn.className = 'btn-primary';
    startBtn.textContent = t('btn_start_game');
    startBtn.onclick = () => {
      this.callbacks.onStartSolo(this.selectedMode, this.selectedBotProfile);
    };

    parent.appendChild(modeSection);
    parent.appendChild(botLabel);
    parent.appendChild(botSelect);
    parent.appendChild(startBtn);
  }

  private renderMultiplayerConfig(parent: HTMLElement): void {
    const content = document.createElement('div');
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.gap = '16px';

    // Game Mode Selector for Multiplayer
    const modeSection = document.createElement('div');
    modeSection.className = 'mode-selector';

    const modes: Array<{ mode: GameMode; title: string; desc: string }> = [
      { mode: 'duel', title: t('mode_duel'), desc: t('mode_duel_desc') },
      { mode: 'teams', title: t('mode_teams'), desc: t('mode_teams_desc') },
      { mode: 'ffa', title: t('mode_ffa'), desc: t('mode_ffa_desc') },
    ];

    modes.forEach((m) => {
      const card = document.createElement('div');
      card.className = `mode-card ${this.selectedMode === m.mode ? 'selected' : ''}`;
      card.innerHTML = `
        <div>
          <div class="mode-name">${m.title}</div>
          <div class="mode-desc">${m.desc}</div>
        </div>
        <div style="font-size: 18px;">${this.selectedMode === m.mode ? '●' : '○'}</div>
      `;
      card.onclick = () => {
        this.selectedMode = m.mode;
        this.render();
      };
      modeSection.appendChild(card);
    });
    content.appendChild(modeSection);

    // Create Room Section
    const createSection = document.createElement('div');
    createSection.style.display = 'flex';
    createSection.style.flexDirection = 'column';
    createSection.style.gap = '10px';

    const createBtn = document.createElement('button');
    createBtn.className = 'btn-primary';
    createBtn.textContent = t('btn_create_room');
    createBtn.onclick = () => {
      this.callbacks.onCreateRoom(this.selectedMode, false);
    };
    createSection.appendChild(createBtn);

    // Join with Code Section
    const joinSection = document.createElement('div');
    joinSection.style.display = 'flex';
    joinSection.style.flexDirection = 'column';
    joinSection.style.gap = '8px';

    const codeRow = document.createElement('div');
    codeRow.className = 'code-input-row';

    const input = document.createElement('input');
    input.className = 'code-input';
    input.placeholder = t('enter_room_code');
    input.maxLength = 12;

    const joinBtn = document.createElement('button');
    joinBtn.className = 'btn-action btn-bit-it';
    joinBtn.textContent = t('btn_join_room');
    joinBtn.onclick = () => {
      if (input.value.trim()) {
        this.callbacks.onJoinRoom(input.value.trim());
      }
    };

    codeRow.appendChild(input);
    codeRow.appendChild(joinBtn);
    joinSection.appendChild(codeRow);

    // Public Rooms Section
    const publicSection = document.createElement('div');
    publicSection.style.display = 'flex';
    publicSection.style.flexDirection = 'column';
    publicSection.style.gap = '8px';

    const publicTitle = document.createElement('h4');
    publicTitle.style.fontSize = '14px';
    publicTitle.style.fontWeight = '700';
    publicTitle.textContent = t('public_rooms');
    publicSection.appendChild(publicTitle);

    if (this.publicRooms.length === 0) {
      const emptyNotice = document.createElement('p');
      emptyNotice.style.fontSize = '12px';
      emptyNotice.style.color = '#71717A';
      emptyNotice.textContent = t('no_public_rooms');
      publicSection.appendChild(emptyNotice);
    } else {
      this.publicRooms.forEach((r) => {
        const item = document.createElement('div');
        item.className = 'mode-card';
        item.innerHTML = `
          <div>
            <div class="mode-name">${r.id} (${r.mode.toUpperCase()})</div>
            <div class="mode-desc">${t('players_joined')}: ${r.players.length}/${r.mode === 'duel' ? 2 : 4}</div>
          </div>
          <button class="btn-action btn-face" style="padding: 6px 12px; font-size: 12px;">${t('btn_join_room')}</button>
        `;
        item.onclick = () => this.callbacks.onJoinRoom(r.id);
        publicSection.appendChild(item);
      });
    }

    content.appendChild(createSection);
    content.appendChild(joinSection);
    content.appendChild(publicSection);
    parent.appendChild(content);
  }

  private renderWaitingRoom(parent: HTMLElement, session: RoomSession): void {
    const isHost = session.hostId === this.localPlayerId;
    const maxPlayers = session.mode === 'duel' ? 2 : 4;

    const roomInfo = document.createElement('div');
    roomInfo.style.display = 'flex';
    roomInfo.style.flexDirection = 'column';
    roomInfo.style.gap = '12px';

    const headerRow = document.createElement('div');
    headerRow.style.display = 'flex';
    headerRow.style.justifyContent = 'space-between';
    headerRow.style.alignItems = 'center';

    headerRow.innerHTML = `
      <div>
        <h3 style="font-size: 18px; font-weight: 800; color: #27272A;">${t('room_code')}: ${session.id}</h3>
        <p style="font-size: 12px; color: #71717A;">${session.isPublic ? t('room_public') : t('room_private')}</p>
      </div>
      <button class="btn-icon" id="btn-copy">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>
        <span>${t('btn_copy_link')}</span>
      </button>
    `;

    const copyBtn = headerRow.querySelector('#btn-copy');
    copyBtn?.addEventListener('click', () => {
      const url = `${window.location.origin}${window.location.pathname}?room=${session.id}`;
      navigator.clipboard.writeText(url);
      alert(t('link_copied'));
    });

    roomInfo.appendChild(headerRow);

    // Players List
    const playerList = document.createElement('div');
    playerList.style.display = 'flex';
    playerList.style.flexDirection = 'column';
    playerList.style.gap = '8px';

    session.players.forEach((p, idx) => {
      const pCard = document.createElement('div');
      pCard.className = 'mode-card';
      const roleBadge = p.id === session.hostId ? `(${t('player_host')})` : p.isBot ? `(${t('player_bot')})` : '';
      pCard.innerHTML = `
        <span style="font-weight: 700; font-size: 14px;">${idx + 1}. ${p.name} ${roleBadge}</span>
        <span style="font-size: 12px; color: #16A34A;">● ${t('status_connected')}</span>
      `;
      playerList.appendChild(pCard);
    });

    roomInfo.appendChild(playerList);

    // Host Controls: Add Bot, Toggle Public, Start Game
    if (isHost) {
      const controls = document.createElement('div');
      controls.style.display = 'flex';
      controls.style.flexDirection = 'column';
      controls.style.gap = '8px';

      if (session.players.length < maxPlayers) {
        const addBotBtn = document.createElement('button');
        addBotBtn.className = 'btn-action btn-bit-it';
        addBotBtn.textContent = t('btn_add_bot');
        addBotBtn.onclick = () => {
          this.callbacks.onAddBotToRoom('minimax');
        };
        controls.appendChild(addBotBtn);
      }

      const togglePublicBtn = document.createElement('button');
      togglePublicBtn.className = 'btn-action btn-fold';
      togglePublicBtn.textContent = session.isPublic ? t('toggle_private') : t('toggle_public');
      togglePublicBtn.onclick = () => {
        this.callbacks.onToggleRoomPublic();
      };
      controls.appendChild(togglePublicBtn);

      const canStart = session.players.length === maxPlayers;
      const startBtn = document.createElement('button');
      startBtn.className = 'btn-primary';
      startBtn.textContent = t('btn_start_game');
      startBtn.disabled = !canStart;
      startBtn.onclick = () => {
        this.callbacks.onStartOnlineGame();
      };
      controls.appendChild(startBtn);

      roomInfo.appendChild(controls);
    } else {
      const waitingNotice = document.createElement('p');
      waitingNotice.style.textAlign = 'center';
      waitingNotice.style.fontSize = '13px';
      waitingNotice.style.color = '#71717A';
      waitingNotice.textContent = t('room_status_waiting');
      roomInfo.appendChild(waitingNotice);
    }

    const leaveBtn = document.createElement('button');
    leaveBtn.className = 'btn-action btn-fold';
    leaveBtn.style.marginTop = '8px';
    leaveBtn.textContent = t('btn_leave');
    leaveBtn.onclick = () => {
      this.callbacks.onLeaveRoom();
    };
    roomInfo.appendChild(leaveBtn);

    parent.appendChild(roomInfo);
  }
}
